import { Input as AntInput, AutoComplete, Col, DatePicker, Form, InputNumber, Radio, Select, Upload } from 'antd';
import { pick } from 'lodash';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useMemo, useState } from 'react';

import 'react-phone-input-2/lib/style.css';

import RichTextEditor from '../../../components/common/RichTextEditor';
import './Input.css';
const PhoneInput = lazy(() => import('react-phone-input-2'));

const Input = React.memo((props) => {
  const { form, type = '' } = props;
  const { getFieldDecorator } = form || {};
  const inputProps = pick(props, [
    'placeholder',
    'className',
    'onChange',
    'onClick',
    'onFocus',
    'onBlur',
    'value',
    'rows',
    'format',
    'disabled',
    'mode',
    'style',
    'height',
    'defaultValue',
    'disabledDate',
    'dataSource',
    'onSelect',
    'onSearch',
    'limit',
    'hideLimit',
    'bordered',
    'onFileSelect',
    'filename',
    'accept',
    'max',
    'min',
    'maxLength',
    'hideMaxLength',
    'prefix',
    'data-cy'
  ]);
  const itemProps = pick(props, [
    'label',
    'colon',
    'help',
    'hasFeedback',
    'validateStatus',
    'required',
    'name',
    'tooltip',
    'filenamePlaceholder'
  ]);
  const colProps = pick(props, ['sm', 'md', 'span']);
  let InputComponent = null;
  if (itemProps.validateStatus === 'error') {
    inputProps.className = `${inputProps.className} error`;
  }
  switch ((type || '').toLowerCase()) {
    case 'select':
      {
        const options = props.options || [];
        InputComponent = useMemo(
          () =>
            React.forwardRef((args, ref) => {
              const [list, setList] = useState([]);
              const onSearch = async (value) => {
                const setResult = (result) => {
                  if (result && Array.isArray(result)) {
                    setList(result);
                  } else {
                    setList([]);
                  }
                };
                try {
                  const data = await options(value, setResult);
                  if (data && Array.isArray(data)) setList(data);
                } catch (e) {
                  console.error(e);
                }
              };
              useEffect(() => {
                if (!list.length && options && !Array.isArray(options) && typeof options === 'function' && typeof args.value === 'object' && 'label' in args.value) {
                  onSearch(args.value.label);
                }
              }, []);
              if (options && !Array.isArray(options) && typeof options === 'function') {
                return (
                  <Select
                    {...inputProps}
                    ref={ref}
                    showSearch
                    optionFilterProp="children"
                    optionLabelProp="label"
                    filterOption={false}
                    labelInValue={typeof args.value === 'object' && 'key' in args.value}
                    {...args}
                    onSearch={onSearch}
                    onSelect={e => {
                      if (args.onSelect) {
                        const item = list.find(elm => elm.value === e.key);
                        if (item) args.onSelect(item);
                      }
                    }}
                  >
                    {list.map((item, i) => (
                      <Select.Option data-cy={item.content || item.label} key={i} value={item.value} label={item.label}>
                        {item.content || item.label}
                      </Select.Option>
                    ))}
                  </Select>
                );
              }
              return (
                <Select
                  {...inputProps}
                  ref={ref}
                  showSearch
                  optionFilterProp="children"
                  optionLabelProp="label"
                  filterOption={(input, option) => {
                    let value = '';
                    if (typeof option.props.children === 'string') {
                      value = option.props.children;
                    } else if (typeof option.props.label === 'string') {
                      value = option.props.label;
                    } else if ('value' in option.props) {
                      value = `${option.props.value}`;
                    }
                    return value.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                  {...args}
                >
                  {options.map((item, i) => (
                    <Select.Option data-cy={item.content || item.label} key={i} value={item.value} label={item.label}>
                      {item.content || item.label}
                    </Select.Option>
                  ))}
                </Select>
              );
            }),
          [...(Array.isArray(options) ? options : [])]
        );
      }
      break;
    case 'radio':
      {
        const options = props.options || [];
        InputComponent = useMemo(
          () =>
            React.forwardRef((args, ref) => (
              <Radio.Group
                {...inputProps}
                ref={ref}
                {...args}
              >
                {options.map((item, i) => (
                  <Radio key={i} value={item.value}>
                    {item.content || item.label}
                  </Radio>
                ))}
              </Radio.Group>
            )),
          [...options]
        );
      }
      break;
    case 'textarea':
      InputComponent = AntInput.TextArea;
      break;
    case 'upload':
      InputComponent = useMemo(
        () =>
          React.forwardRef((args, ref) => {
            const [fileobj, setCertificateFile] = useState(null);
            const [filename, setFileName] = useState(inputProps.filename || null);
            useEffect(() => {
              if (fileobj) { setFileName(fileobj.name); }
            }, [fileobj]);

            const beforeUpload = (_file) => {
              setCertificateFile(_file);
              return false;
            };
            const onChange = ({ file }) => {
              if (args.onFileSelect) { args.onFileSelect(file); }
            };
            return (

              <Upload ref={ref}
                multiple={false}
                showUploadList={false}
                maxCount={1}
                {...inputProps}
                onChange={onChange}
                beforeUpload={beforeUpload}
              >
                <div className="uploadwrapper">
                  <div className="uploadfilename">{filename || itemProps.filenamePlaceholder}</div>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', marginRight: '8px' }}>
                      <span className="material-icons fileuploadicon">file_upload</span>
                      <div className="fileuploadtext">{filename ? 'Replace' : 'Upload'}</div>
                    </div>
                    {
                      filename
                        ? <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange({ file: null }); setFileName(null); }} style={{ color: '#F86567' }} className="material-icons" aria-hidden="true">cancel</span>
                        : null
                    }
                  </div>
                </div>
              </Upload>

            );
          }),
        []
      );
      break;
    case 'editor':
      InputComponent = RichTextEditor;
      break;
    case 'tel':
      InputComponent = useMemo(
        () =>
          React.forwardRef((args, ref) => {
            // console.log(args)
            const { phoneNumber/*, countryCode */ } = {
              phoneNumber: args.value && args.value.phoneNumber ? args.value.phoneNumber : '',
              countryCode: args.value && args.value.countryCode ? args.value.countryCode : 'us',
              ...args.value
            };
            const handlePhoneChange = (value, data) => {
              if (args.onChange) {
                args.onChange({
                  phoneNumber: value,
                  countryCode: data && data.countryCode
                });
              }
            };
            return (
              <div className="phone-input">
                <PhoneInput
                  ref={ref}
                  inputProps={{}}
                  country={'us'}
                  defaultValue={phoneNumber}
                  value={phoneNumber || ''}
                  onChange={handlePhoneChange}
                  data-testid="phoneNumber"
                  enableSearch={true}
                  enableAreaCodes={true}
                  enableLongNumbers={true}
                  // enableAreaCodeStretch
                  // inputProps={phoneNumber && { value: phoneNumber }}
                  inputClass="ant-select"
                  inputStyle={{ height: 40 }}
                />
              </div>
            );
          }),
        []
      );
      break;
    case 'month':
      InputComponent = DatePicker.MonthPicker;
      break;
    case 'autocomplete':
      InputComponent = AutoComplete;
      break;
    case 'password':
      InputComponent = AntInput.Password;
      break;
    default:
      InputComponent = props.component ? props.component : AntInput;
      break;
  }

  return (
    <Col {...colProps}>
      <Form.Item {...itemProps}>
        {getFieldDecorator
          ? (
            getFieldDecorator(props.name, {
              rules: props.rules,
              initialValue: props.initialValue,
            })(<InputComponent {...inputProps} />)
          )
          : (
            props.maxLength ? (
              <div className="inputmaxlengthdiv">
                <InputComponent {...inputProps} />
                {!props.hideMaxLength ? <small className="inputmaxlength">{inputProps.value ? `${inputProps.value}`.length : 0}/{props.maxLength}</small> : null}
              </div>
            ) : <InputComponent {...inputProps} />)}
        {props.subText ? <small className="inputsubtext">{props.subText}</small> : null}
      </Form.Item>
    </Col>
  );
});

Input.Select = (props) => <Input {...props} type="Select" />;
Input.TextArea = (props) => <Input {...props} type="TextArea" />;
Input.Editor = (props) => <Input {...props} type="Editor" />;
Input.Tel = (props) => <Input {...props} type="Tel" />;
Input.Month = (props) => <Input {...props} type="month" />;
Input.AutoComplete = (props) => <Input {...props} type="autocomplete" />;
Input.Radio = (props) => <Input {...props} type="radio" />;
Input.Password = (props) => <Input {...props} type="password" />;
Input.Upload = (props) => <Input {...props} type="upload" />;
Input.Number = (props) => <InputNumber {...props} />;

Input.propTypes = {
  form: PropTypes.object,
  type: PropTypes.string,
  maxLength: PropTypes.any,
  hideMaxLength: PropTypes.bool,
  options: PropTypes.any,
  component: PropTypes.any,
  name: PropTypes.oneOfType([
    PropTypes.string, 
    PropTypes.array,
  ]),
  rules: PropTypes.any,
  initialValue: PropTypes.any,
};

export default Input;
